import { useState, type ReactNode } from "react";

interface DragReorderListProps<T> {
  items: T[];
  getId?: (item: T) => string;
  onReorder: (orderedIds: string[]) => void;
  renderItem: (item: T) => ReactNode;
  className?: string;
}

/**
 * Native HTML5 drag-and-drop single-axis reorder list. `items` must have
 * stable `id`s; calls `onReorder(orderedIds)` on drop.
 */
export function DragReorderList<T extends { id: string }>({
  items,
  getId = (item) => item.id,
  onReorder,
  renderItem,
  className = "",
}: DragReorderListProps<T>) {
  const [dragId, setDragId] = useState<string | null>(null);

  const handleDrop = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const ids = items.map(getId);
    const fromIndex = ids.indexOf(dragId);
    const toIndex = ids.indexOf(targetId);
    ids.splice(fromIndex, 1);
    ids.splice(toIndex, 0, dragId);
    onReorder(ids);
    setDragId(null);
  };

  return (
    <div className={className}>
      {items.map((item) => {
        const id = getId(item);
        return (
          <div
            key={id}
            draggable
            onDragStart={() => setDragId(id)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => handleDrop(id)}
            style={{ opacity: dragId === id ? 0.5 : 1 }}
          >
            {renderItem(item)}
          </div>
        );
      })}
    </div>
  );
}
